import { FC } from 'react';
import { Box, Flex, Palette, Text } from '@updater/ui-uds';
import {
  TvInternetOffer,
  ServiceabilityPricing,
  ServiceabilityChargeType,
} from '@updater/consumer-graph';
import { useOfferContext, useProviderContext } from '@updater/ui-widgets';
import styled from '@emotion/styled';
import { getProviderConfig } from 'config';

const Dot = styled.div<{ isActive?: boolean }>`
  width: 10px;
  height: 10px;
  border-radius: 50%;
  background-color: ${({ isActive = true }) =>
    isActive ? Palette.mint500 : Palette.gray400};
  flex-shrink: 0;
`;

const Line = styled.div<{ isActive?: boolean }>`
  width: 2px;
  flex: 1;
  min-height: 8px;
  background-color: ${({ isActive = true }) =>
    isActive ? Palette.mint500 : Palette.gray400};
`;

interface PriceStep {
  label: string;
  price: number;
}

function getStepsFromCharges(
  charges: ServiceabilityPricing['charges']
): PriceStep[] {
  if (!charges) return [];

  const shortTerm = charges.find(
    (c) => c?.type === ServiceabilityChargeType.short_term
  );
  const longTerm = charges.find(
    (c) => c?.type === ServiceabilityChargeType.long_term
  );

  const steps: PriceStep[] = [];

  if (shortTerm?.amount != null && shortTerm?.term?.length) {
    steps.push({
      label: `Month 1-${shortTerm.term.length}`,
      price: shortTerm.amount,
    });
  }

  if (longTerm?.amount != null) {
    const startMonth = shortTerm?.term?.length ? shortTerm.term.length + 1 : 1;
    steps.push({
      label: `Month ${startMonth}+`,
      price: longTerm.amount,
    });
  }

  return steps;
}

export const PriceOverTime: FC = () => {
  const { pricing } = useOfferContext() as TvInternetOffer;
  const { code: providerCode } = useProviderContext();
  const showTaxesAndFees = getProviderConfig({ providerCode }).showTaxesAndFees;
  const steps = getStepsFromCharges(pricing?.charges);

  if (steps.length < 2) return null;

  return (
    <Box mb="m">
      <Text variant="mBold" mb="s">
        Your price over time
      </Text>
      <Box backgroundColor={Palette.blue50 as any} borderRadius="s" padding="m">
        {steps.map((step, index) => (
          <Flex key={step.label} alignItems="stretch">
            <Flex
              flexDirection="column"
              alignItems="center"
              mr="s"
              width="10px"
            >
              <Line isActive={index === 0} />
              <Dot isActive={index === 0} />
              <Line isActive={index < steps.length - 1} />
            </Flex>
            <Box py="xxs">
              <Text variant="xs" color={Palette.gray600}>
                {step.label}
              </Text>
              <Flex alignItems="baseline">
                <Text
                  variant="lBold"
                  color={index === 0 ? Palette.mint700 : Palette.gray700}
                >
                  ${Math.round(step.price)}
                </Text>
                <Text
                  variant="s"
                  color={index === 0 ? Palette.mint700 : Palette.gray700}
                  ml="xxs"
                >
                  /mo
                </Text>
              </Flex>
              {showTaxesAndFees && (
                <Text
                  variant="xxs"
                  color={Palette.gray600}
                  data-testid={`taxes-and-fees-${step.label}`}
                >
                  + taxes & fees
                </Text>
              )}
            </Box>
          </Flex>
        ))}
      </Box>
    </Box>
  );
};
